import { Fragment, useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import { useParams } from 'react-router-dom';
import {
  COOKIES_CONFIG,
  PATHS,
  useModal,
  trackMixpanelEvent,
} from '@belong/common';
import { Text, Toast, Image } from '@belong/ui';
import { Fallback } from 'accounts/components/fallback';
import { MeetYourTeamSection } from 'accounts/components/meet-your-team-section';
import { MyHomeTimeline } from 'accounts/components/my-home-timeline/my-home-timeline';
import { SetupFlowSections } from 'accounts/components/setup-flow-sections';
import { PROPERTY_PLACEMAT_STRINGS } from 'accounts/constants/strings/placemat.strings';
import { SetupFlowProvider } from 'accounts/contexts/setup-flow';
import { useCheckPropertyIdParam } from 'accounts/hooks/homeowners/use-check-property-id-param';
import { useSelectedProperty } from 'accounts/hooks/homeowners/use-selected-property';
import { useSetupFlowSections } from 'accounts/hooks/homeowners/use-setup-flow-sections';
import { useSteps } from 'accounts/hooks/homeowners/use-steps';
import { useUnits } from 'accounts/hooks/homeowners/use-units';
import { isCurrentPropertyOrUnitStep } from 'accounts/utils/flow-steps';
import { ActionItemCountLink } from 'action-items/components/action-item-count-link/action-item-count-link';
import { getActionItems, getPropertyActionItems } from 'action-items/utils';
import type { UserModel } from 'api/models';
import clsx from 'clsx';
import { GENERAL_ICONS } from 'components/GeneralIcon/GeneralIcon';
import IconButton, { ALIGN_TYPES } from 'components/IconButton/IconButton';
import Spinner from 'components/Spinner/Spinner';
import { HomeInfo } from 'components/home-info/home-info';
import LinkBase from 'corecomponents/LinkBase/LinkBase';
import { useFeatureFlag } from 'hooks/use-account-redesign';
import FormLayout from 'layouts/FormLayout/FormLayout';
import { FlowStatus, FlowType } from 'models/enums';
import { parseCookies } from 'nookies';
import InitialEstimateModal from 'pages/Accounts/Components/InitialEstimateModal/InitialEstimateModal';
import { QUERY_PARAMS } from 'pages/PostInspectionFlow/utils';
import { BASE_PATHS } from 'routes/paths';
import {
  selectAccountProperties,
  selectAccountPropertySummaries,
} from 'store/redux/homeowner-accounts/selectors';
import { selectInspections } from 'store/redux/inspections';
import {
  selectUser,
  selectUserFlows,
  selectUserToDos,
} from 'store/redux/user/selectors';
import { formatString } from 'strings';
import { formatAddress } from 'utils/formatAddress';
import styles from './setup-flow.module.css';
import { fetchWealth } from 'store/redux/homeowner-accounts/actions';
import { useAppDispatch } from 'store/redux/use-app-dispatch';

const { Title } = FormLayout;

export function HomeownersSetupFlowPage() {
  const dispatch = useAppDispatch();
  const properties = useSelector(selectAccountProperties);
  const propertySummaries = useSelector(selectAccountPropertySummaries);
  const flows = useSelector(selectUserFlows);
  const history = useHistory();
  const location = useLocation();
  const [wealth, setWealth] = useState();
  const user = useSelector(selectUser) as unknown as UserModel;
  const { units, unitIds } = useUnits();
  const todos = useSelector(selectUserToDos);
  const inspections = useSelector(selectInspections);
  const { isEnabled: isAccountRedesignEnabled } =
    useFeatureFlag('account-redesign');
  const { propertyId } = useParams<{ propertyId?: string }>();
  const { selectedProperty } = useSelectedProperty();
  const [isToastOpen, setIsToastOpen] = useState(() => {
    const parsedURL = new URLSearchParams(location.search);
    const reportApprovedQueryParam = parsedURL.get(
      QUERY_PARAMS.REPORT_APPROVED,
    );

    return reportApprovedQueryParam === 'true';
  });

  let propertyActionItems = [];

  if (
    Array.isArray(properties) &&
    Array.isArray(flows) &&
    Array.isArray(todos) &&
    Array.isArray(inspections) &&
    Array.isArray(wealth) &&
    typeof user !== 'undefined'
  ) {
    const actionItems = getActionItems({
      properties: propertySummaries,
      flows,
      todos,
      user,
      inspections,
      wealth: wealth ?? [],
    });

    const selectedPropertySummary = propertySummaries.find(
      (property) => property.basicInfo!.propertyId === propertyId,
    );

    propertyActionItems = getPropertyActionItems({
      // `getPropertyActionItems` dereferences this directly, so a summary that
      // does not match already threw here.
      property: selectedPropertySummary!,
      actionItems,
    });
  }

  const setupFlows = flows?.filter((flow: any) => {
    if (
      flow.flowType === FlowType.HomeownerSetup ||
      flow.flowType === FlowType.HomeInspection ||
      flow.flowType === FlowType.HomeListing
    ) {
      const isCurrentPropertyOrUnit = flow.steps?.some((step: any) =>
        isCurrentPropertyOrUnitStep(step, selectedProperty),
      );
      return isCurrentPropertyOrUnit;
    }

    if (flow.flowType === FlowType.UserPayment) {
      return flow.steps?.some(
        (step: any) => step.dataUniqueId === user?.userId,
      );
    }

    return false;
  });

  const { steps, allStepsByGate } = useSteps({ setupFlows, properties });

  const { propertySection, accountSection, shouldShowCommonSteps } =
    useSetupFlowSections({
      properties,
      allStepsByGate,
      units,
    });

  const [isInitialModalOpen, openInitialModal, closeInitialModal] = useModal();
  const [selectedUnit, setSelectedUnit] = useState();
  const [selectedAddress, setSelectedAddress] = useState<string>();
  const [selectedAddressModel, setSelectedAddressModel] = useState<string>();

  const cookies = parseCookies();

  const comeFromTheMobileApp = Boolean(
    cookies[COOKIES_CONFIG.MOBILE_APP.name] === 'true',
  );
  const isLoading = !selectedProperty;

  useCheckPropertyIdParam();

  useEffect(() => {
    if (selectedProperty && !isLoading) {
      trackMixpanelEvent('flow-started', {
        flowType: 'HomeownerSetup',
        homeId: selectedProperty.basicInfo.propertyId,
      });
    }
  }, [selectedProperty, isLoading]);

  function handleAddHomeClick() {
    history.push(PATHS.HOMEOWNER_ONBOARDING_PROPERTY_ADDRESS);
  }

  function getTitle() {
    const isAdoptedResident = selectedProperty?.units?.some(
      (unit: any) => unit.isAdoptedAgreement,
    );
    if (isAdoptedResident) {
      return PROPERTY_PLACEMAT_STRINGS['title.adopted_resident'];
    }

    return PROPERTY_PLACEMAT_STRINGS[
      'title.not_adopted_resident_and_home_not_loved'
    ];
  }

  useEffect(() => {
    if (selectedProperty && !selectedProperty.getUnitsWithAgreements().length) {
      history.push(BASE_PATHS.HOMEOWNER_SETUP_FLOW);
    }
  }, [history, selectedProperty]);

  function openModalWithUnit(unit: any, address: any) {
    const formattedAddress = formatAddress(
      address,
      unit?.basicInfo?.unitNumber,
    );

    setSelectedUnit(unit);
    setSelectedAddress(formattedAddress);
    setSelectedAddressModel(address);
    openInitialModal();
  }

  function calculateFlowProgress(betterRenewalFlow: any) {
    if (!betterRenewalFlow) return 0;

    const missedPayments = betterRenewalFlow.steps.filter(
      (step: any) => step.stepName === 'MissedPayments',
    );
    if (missedPayments.isHidden) return 0;
    const flowSteps = betterRenewalFlow.steps.filter(
      (step: any) => !step.isHidden,
    );
    const completedSteps = flowSteps.filter(
      (step: any) => step.status === FlowStatus.Completed,
    );
    const progress = (completedSteps.length * 100) / flowSteps.length;
    return progress;
  }

  const flowSection: {
    progressPercentage?: number;
    disabled?: boolean;
    displayName?: string;
    reportLink?: string;
  } = {};

  const hasBetterRenewals = isAccountRedesignEnabled
    ? null
    : flows.find((flow: any) => flow.flowType === 'HomeownerRenewal');

  if (hasBetterRenewals) {
    flowSection.progressPercentage = calculateFlowProgress(hasBetterRenewals);
    flowSection.disabled =
      hasBetterRenewals.status === FlowStatus.Completed ||
      hasBetterRenewals.status === FlowStatus.Cancelled;
    flowSection.displayName = 'Review Renewal Offer';
    flowSection.reportLink = `${PATHS.HO_BETTER_RENEWALS_FLOW}?flowId=${hasBetterRenewals.uniqueId}`;
  }

  function handleToastClose() {
    setIsToastOpen(false);
  }

  useEffect(() => {
    async function init() {
      const wealthData = await dispatch(fetchWealth());

      setWealth(wealthData);
    }

    init();
  }, [dispatch]);

  return (
    <SetupFlowProvider
      properties={properties}
      steps={steps}
      allStepsByGate={allStepsByGate}
      units={units}
      unitIds={unitIds}
      loading={isLoading}
    >
      <FormLayout>
        <header className="flex flex-wrap-reverse items-end justify-between md:mb-[4rem] md:flex-nowrap">
          <Title
            header
            title={getTitle()}
            className="mb-sm w-full border-0 border-b border-solid border-gray pb-sm md:mb-0 md:w-auto md:border-0 md:pb-0"
          />
          {!comeFromTheMobileApp && (
            <div className="mb-xl flex w-full justify-end md:mb-0 md:w-auto md:self-end">
              <IconButton
                paddingCustomClassName="leading-[22px]"
                align={ALIGN_TYPES.RIGHT}
                onClick={handleAddHomeClick}
                label="ADD HOME"
                icon={GENERAL_ICONS.PLUS}
                generalIconProps={{
                  scale: 0.9,
                }}
              />
            </div>
          )}
        </header>
        {isAccountRedesignEnabled && (
          <div className="mb-xl">
            <ActionItemCountLink
              count={propertyActionItems.length}
              propertyId={propertyId}
            />
          </div>
        )}
        {hasBetterRenewals && (
          <div className="mb-3xl">
            <div className="mb-sm">
              <Text variant="h3" fontWeight="semibold">
                Review Renewal Offer
              </Text>
            </div>
            <LinkBase
              disabled={flowSection.disabled}
              className={clsx(
                styles.flowCard,
                'relative flex flex-wrap items-center justify-between',
                {
                  'bg-green-light-bg': flowSection.progressPercentage === 100,
                  'cursor-pointer': !flowSection.disabled,
                },
              )}
              {...(!flowSection.disabled && {
                href: flowSection.reportLink,
              })}
            >
              <div
                className={clsx(
                  'mr-sm flex items-center text-body font-semibold',
                  {
                    'text-dark-gray': flowSection.disabled,
                    'text-navy': !flowSection.disabled,
                  },
                )}
              >
                <Text fontWeight="semibold" className="mr-xs">
                  {formatString(flowSection.displayName)}
                </Text>
              </div>
              <div className="mt-sm flex w-full items-center md:mt-0 md:w-auto">
                {flowSection.disabled ? (
                  <>
                    {flowSection.progressPercentage === 100 ? (
                      <>
                        <Text fontWeight="semibold" className="text-green">
                          100%&nbsp;
                        </Text>
                        <Text fontWeight="semibold" className="text-dark-gray">
                          Complete
                        </Text>
                      </>
                    ) : (
                      <Text fontWeight="semibold" className="text-dark-gray">
                        Not Ready
                      </Text>
                    )}
                  </>
                ) : (
                  <>
                    <Text
                      fontWeight="semibold"
                      className={clsx('mr-2xs', {
                        'text-green': flowSection.progressPercentage === 100,
                        'text-red': flowSection.progressPercentage! < 100,
                      })}
                    >
                      {flowSection.progressPercentage}%
                    </Text>
                    <Text fontWeight="semibold">Complete</Text>
                  </>
                )}
                <IconButton
                  disabled={flowSection.disabled}
                  align={ALIGN_TYPES.LEFT}
                  icon={GENERAL_ICONS.FRONT_ARROW}
                />
              </div>
            </LinkBase>
          </div>
        )}
        {isLoading ? (
          <Spinner />
        ) : (
          <Fragment>
            <ErrorBoundary FallbackComponent={Fallback}>
              <SetupFlowSections
                properties={properties}
                accountSection={accountSection}
                propertySection={propertySection}
                shouldShowCommonSteps={shouldShowCommonSteps}
                openInitialModal={openModalWithUnit}
              />
              <HomeInfo units={units} accountProperty={selectedProperty} />
              <MeetYourTeamSection />
              <div className="mb-3xl">
                <MyHomeTimeline />
              </div>
            </ErrorBoundary>
          </Fragment>
        )}
      </FormLayout>
      <InitialEstimateModal
        todos={null}
        address={selectedAddress}
        unit={selectedUnit}
        isOpen={isInitialModalOpen}
        onDismiss={closeInitialModal}
        addressModel={selectedAddressModel}
      />
      <Toast
        icon={
          <Image
            alt=""
            className="object-scale-down"
            src="icons/alert-white.svg"
          />
        }
        isVisible={isToastOpen}
        onClose={handleToastClose}
        variant="danger"
      >
        {`You cannot modify an approved report. If you'd like to make changes, please contact your homeowner\u00a0advisor.`}
      </Toast>
    </SetupFlowProvider>
  );
}
